import { Box, Text, Tooltip } from "@mantine/core";
import { LagStatus, useFirestoreLag } from "../hooks/useFirestoreLag";

type StatusMeta = {
  label: string;
  description: string;
  color: string;
};

const STATUS_META: Record<LagStatus, StatusMeta> = {
  ok: {
    label: "Live",
    description: "Realtime updates are current.",
    color: "#22c55e",
  },
  degraded: {
    label: "Lagging",
    description: "Data is arriving, but with noticeable delay.",
    color: "#eab308",
  },
  stale: {
    label: "Stale",
    description: "Data is more than 30 seconds behind.",
    color: "#ef4444",
  },
  offline: {
    label: "Offline",
    description: "Client is offline or not receiving updates.",
    color: "#9ca3af",
  },
};

export function LagPill() {
  const { status, lagMs, fromCache } = useFirestoreLag();

  const meta = STATUS_META[status];

  const lagSeconds =
    typeof lagMs === "number" ? Math.max(0, Math.round(lagMs / 1000)) : null;

  const details: string[] = [];
  if (status === "offline") {
    details.push("No connection");
  } else {
    if (lagSeconds !== null) {
      details.push(`${lagSeconds}s lag`);
    } else {
      details.push("syncing...");
    }

    if (fromCache) {
      details.push("cache");
    }
  }

  const detailText = details.join(" | ");
  const background = "#f8fafc";
  const border = "#e2e8f0";
  const mutedColor = "#6b7280";

  const tooltipLabel = `${meta.description}${
    detailText ? ` (${detailText})` : ""
  }`;

  return (
    <Tooltip
      label={tooltipLabel}
      withArrow
      position="bottom-end"
    >
      <Box
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 10px",
          borderRadius: 999,
          backgroundColor: background,
          border: `1px solid ${border}`,
          lineHeight: 1,
        }}
      >
        <Box
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: meta.color,
          }}
        />
        <Text size="xs" fw={600} c={meta.color}>
          {meta.label}
        </Text>
        {detailText && (
          <Text size="xs" c={mutedColor}>
            {detailText}
          </Text>
        )}
      </Box>
    </Tooltip>
  );
}
